import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";

import Header from "@components/shared/header";

import { useDomainContext } from "@hooks/useDomainContext";
import TylenolAutismLogo from "@components/tylenolautism/logo";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";

const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const Modal = dynamic(() => import("@components/shared/modal"));

const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

const Footer = dynamic(() => import("@components/shared/footer"));

export default function TylenolAutismWrapper({
    children,
}: {
    children: ReactNode;
}): ReactElement {
    const { modal, closeModal } = useModal();
    const {
        visitDetails: { region },
    } = useDomainContext();
    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return (
                    <PrivacyPolicy
                        category="Tylenol"
                        domain="TylenolAutism.claims"
                    />
                );
            case "terms":
                return (
                    <TermsAndConditions
                        category="Tylenol"
                        domain="TylenolAutism.claims"
                    />
                );
            case "sideWidget":
                return <Mobile />;
            default:
                return null;
        }
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
                    rel="stylesheet"
                ></link>
            </Head>

            <div style={{ fontFamily: `'Inter', sans-serif` }}>
                <Header
                    slogan={`the best tylenol autism lawyers ${
                        region ? `in ${region}` : ""
                    }`}
                >
                    <TylenolAutismLogo />
                </Header>
                <main className="max-w-7xl mx-auto block px-4vw lg:px-4">
                    {children}
                </main>
                <Footer />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={
                        modalContent === "privacy"
                            ? "Privacy Policy"
                            : modalContent === "terms"
                            ? "Terms and Conditions"
                            : modalContent === "sideWidget"
                            ? "Contents"
                            : undefined
                    }
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
